/**
 * McIvor Sales: makes the header logo a push button that opens the site
 * navigation drawer. It reuses the theme's existing <header-drawer> logic by
 * activating the drawer's own <summary> toggle (same path as the hamburger),
 * and keeps the logo button's aria-expanded / aria-label in sync.
 */
const TOGGLE_SELECTOR = '[data-mc-logo-menu-toggle]';
const DETAILS_ID = 'Details-menu-drawer-container';

/** @type {HTMLElement | null} */
let lastOpener = null;
/** @type {MutationObserver | null} */
let detailsObserver = null;
/** @type {Element | null} */
let observedDetails = null;
let wasOpen = false;

function getDetails() {
  return document.getElementById(DETAILS_ID);
}

function isOpen() {
  return Boolean(getDetails()?.hasAttribute('open'));
}

/** @param {Element} el */
function isVisible(el) {
  if (typeof el.checkVisibility === 'function') {
    return el.checkVisibility({ checkOpacity: false, checkVisibilityCSS: true, visibilityProperty: true });
  }
  return el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
}

/** @param {Element} container */
function visibleFocusables(container) {
  const selector =
    "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), input:not([type=hidden]):enabled, select:enabled, textarea:enabled";
  return Array.from(container.querySelectorAll(selector)).filter(
    (el) => el instanceof HTMLElement && isVisible(el)
  );
}

function syncToggles() {
  const open = isOpen();
  document.querySelectorAll(TOGGLE_SELECTOR).forEach((btn) => {
    if (!(btn instanceof HTMLElement)) return;
    btn.setAttribute('aria-expanded', String(open));
    const label = open ? btn.dataset.labelClose : btn.dataset.labelOpen;
    if (label) btn.setAttribute('aria-label', label);
  });

  // Return focus to the logo when the drawer it opened closes.
  if (wasOpen && !open && lastOpener && lastOpener.isConnected) {
    const active = document.activeElement;
    const details = getDetails();
    if (!active || active === document.body || (details && details.contains(active))) {
      lastOpener.focus({ preventScroll: true });
    }
    lastOpener = null;
  }
  wasOpen = open;
}

function observeDetails() {
  const details = getDetails();
  if (!details || details === observedDetails) return;
  detailsObserver?.disconnect();
  detailsObserver = new MutationObserver(syncToggles);
  detailsObserver.observe(details, { attributes: true, attributeFilter: ['open', 'class'] });
  observedDetails = details;
  syncToggles();
}

document.addEventListener('click', (event) => {
  const target = event.target;
  const btn = target instanceof Element ? target.closest(TOGGLE_SELECTOR) : null;
  if (!(btn instanceof HTMLElement)) return;

  const details = getDetails();
  const summary = details?.querySelector(':scope > summary');
  if (!details || !(summary instanceof HTMLElement)) return; // no menu on this page: do nothing

  event.preventDefault();
  observeDetails();
  const opening = !details.hasAttribute('open');
  if (opening) lastOpener = btn;

  // Same code path as tapping the theme's hamburger toggle.
  summary.click();

  if (opening) {
    // The theme's focus trap focuses the first focusable (the hamburger summary),
    // which is hidden on desktop; move focus into the drawer explicitly.
    const focusDrawer = () => {
      const drawer = details.querySelector('.menu-drawer');
      if (!drawer || !details.hasAttribute('open')) return;
      if (drawer.contains(document.activeElement)) return;
      const first = visibleFocusables(drawer)[0];
      first?.focus({ preventScroll: true });
    };
    requestAnimationFrame(() => requestAnimationFrame(focusDrawer));
    setTimeout(focusDrawer, 450);
  }
});

// Keep Tab / Shift+Tab cycling within the visible drawer controls while it is open
// (covers desktop, where the hamburger summary is hidden).
document.addEventListener(
  'keydown',
  (event) => {
    if (event.key !== 'Tab' || !isOpen()) return;
    const details = getDetails();
    const drawer = details?.querySelector('.menu-drawer');
    if (!drawer) return;
    const items = visibleFocusables(drawer);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (!event.shiftKey && (active === last || !drawer.contains(active))) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && (active === first || !drawer.contains(active))) {
      event.preventDefault();
      last.focus();
    }
  },
  true
);

observeDetails();
document.addEventListener('DOMContentLoaded', observeDetails);
// Re-bind if the header section is re-rendered (theme editor).
document.addEventListener('shopify:section:load', observeDetails);
